import React, { useState } from 'react';
import QRCode from 'react-qr-code';

const QRCodeGenerator = () => {
  const [spotId, setSpotId] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [qrCodes, setQrCodes] = useState([]);

  // This should match your actual app URL
  const baseUrl = window.location.origin;

  const generateQRCode = () => {
    if (!spotId.trim()) {
      alert('Please enter a parking spot ID');
      return;
    }

    const url = `${baseUrl}/checkin/${spotId}`;
    setGeneratedUrl(url);

    // Add to list of generated codes
    setQrCodes([...qrCodes, { spotId, url, timestamp: new Date() }]);
    setSpotId('');
  };

  const downloadQRCode = (spotId) => {
    const svg = document.getElementById(`qr-${spotId}`);
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = `parking-spot-${spotId}-qr.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const deleteQRCode = (index) => {
    const newQrCodes = qrCodes.filter((_, i) => i !== index);
    setQrCodes(newQrCodes);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#212529', marginBottom: '8px' }}>QR Code Generator</h2>
        <p style={styles.description}>
          Generate QR codes for each parking spot. Customers can scan these codes to begin the booking process.
        </p>
      </div>

      <div style={styles.generatorBox}>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Parking Spot ID</label>
          <input
            type="text"
            value={spotId}
            onChange={(e) => setSpotId(e.target.value)}
            placeholder="e.g., A1, B2, C3"
            style={styles.input}
            onKeyPress={(e) => e.key === 'Enter' && generateQRCode()}
          />
        </div>
        <button onClick={generateQRCode} style={styles.generateBtn}>
          Generate QR Code
        </button>
      </div>

      {generatedUrl && (
        <div style={styles.previewBox}>
          <h3>Latest Generated QR Code</h3>
          <div style={styles.qrDisplay}>
            <QRCode
              id={`qr-latest`}
              value={generatedUrl}
              size={200}
              level="H"
            />
            <div style={styles.urlDisplay}>
              <strong>URL:</strong> {generatedUrl}
            </div>
          </div>
        </div>
      )}

      {qrCodes.length > 0 && (
        <div style={styles.qrList}>
          <h3>Generated QR Codes</h3>
          <div style={styles.qrGrid}>
            {qrCodes.map((qr, index) => (
              <div key={index} style={styles.qrCard}>
                <div style={styles.qrCardHeader}>
                  <h4 style={styles.spotIdTitle}>Spot: {qr.spotId}</h4>
                  <button
                    onClick={() => deleteQRCode(index)}
                    style={styles.deleteBtn}
                  >
                    ×
                  </button>
                </div>
                <div style={styles.qrCardBody}>
                  <QRCode
                    id={`qr-${qr.spotId}`}
                    value={qr.url}
                    size={150}
                    level="H"
                  />
                </div>
                <div style={styles.qrCardFooter}>
                  <div style={styles.url}>{qr.url}</div>
                  <button
                    onClick={() => downloadQRCode(qr.spotId)}
                    style={styles.downloadBtn}
                  >
                    Download PNG
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {qrCodes.length === 0 && !generatedUrl && (
        <div style={styles.emptyState}>
          <p>No QR codes generated yet. Enter a parking spot ID to begin.</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  description: {
    color: '#6c757d',
    fontSize: '15px',
    marginTop: '10px',
    lineHeight: '1.5',
  },
  generatorBox: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '30px',
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-end',
  },
  inputGroup: {
    flex: 1,
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  generateBtn: {
    padding: '12px 30px',
    backgroundColor: '#1e3a5f',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.2s ease',
  },
  previewBox: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '30px',
    textAlign: 'center',
  },
  qrDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    marginTop: '20px',
  },
  urlDisplay: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    wordBreak: 'break-all',
  },
  qrList: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  qrGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '20px',
    marginTop: '20px',
  },
  qrCard: {
    border: '1px solid #ddd',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
  },
  qrCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px',
    backgroundColor: '#f0f0f0',
    borderBottom: '1px solid #ddd',
  },
  spotIdTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#333',
  },
  deleteBtn: {
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qrCardBody: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'white',
  },
  qrCardFooter: {
    padding: '15px',
    borderTop: '1px solid #ddd',
  },
  url: {
    fontSize: '12px',
    color: '#666',
    wordBreak: 'break-all',
    marginBottom: '10px',
  },
  downloadBtn: {
    width: '100%',
    padding: '8px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#999',
    fontSize: '18px',
  },
};

export default QRCodeGenerator;
